import React, { useState, useCallback, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Grid from '@mui/material/Grid';
import CheckCircle from '@mui/icons-material/CheckCircle';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import { useBarcodeScanner } from './useBarcodeScanner';
import SignaturePad, { SignaturePadHandle } from './SignaturePad';
import ManualScanInput from './ManualScanInput';
import { api } from '../../config/api';

type LoadingState = 'delivery-list' | 'session-active' | 'complete-check' | 'signature' | 'done';

interface SessionPallet {
  sscc: string;
  scanned_at: string | null;
  scan_method: string | null;
  customer_code: string;
  job_number: string | null;
  pallet_number: number | null;
  quantity: number | null;
  stock_code: string | null;
  product_description: string | null;
}

interface LoadingSession {
  id: number;
  customer_code: string;
  license_plate: string;
  expected_pallets: number;
  loaded_pallets: number;
  status: string;
  started_at: string;
  pallets?: SessionPallet[];
}

interface Delivery {
  customer_code: string;
  total_pallets: number;
  staged_count: number;
  loaded_count: number;
  session_id: number | null;
  license_plate: string | null;
  status: 'ready' | 'in_progress';
}

const OPERATOR_NAME_KEY = 'wms_operator_name';

/**
 * iPad-optimized loading screen for despatch operators.
 *
 * Five-state workflow:
 * 1. Delivery list - shows today's deliveries with progress stats
 * 2. Session active - live loading checklist with pallet scanning
 * 3. Complete check - all pallets scanned, ready to complete
 * 4. Signature - driver signs on signature pad
 * 5. Done - success confirmation, auto-returns to delivery list
 */
export default function LoadingScreen() {
  const [loadingState, setLoadingState] = useState<LoadingState>('delivery-list');
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [activeSession, setActiveSession] = useState<LoadingSession | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Dialog state
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [dialogCustomer, setDialogCustomer] = useState<string>('');
  const [licensePlate, setLicensePlate] = useState('');

  // End session dialog
  const [showEndDialog, setShowEndDialog] = useState(false);

  const signaturePadRef = useRef<SignaturePadHandle>(null);
  const operatorName = localStorage.getItem(OPERATOR_NAME_KEY) || '';

  // Refs for stale closure avoidance
  const loadingStateRef = useRef<LoadingState>(loadingState);
  useEffect(() => { loadingStateRef.current = loadingState; }, [loadingState]);

  const activeSessionIdRef = useRef<number | null>(activeSessionId);
  useEffect(() => { activeSessionIdRef.current = activeSessionId; }, [activeSessionId]);

  // Fetch sessions on mount and when returning to delivery-list
  const fetchSessions = useCallback(async () => {
    try {
      const response = await api.get('/wms/loading-sessions');
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setDeliveries(data.deliveries || []);
      }
    } catch {
      // Silent fail — show empty list
    }
  }, []);

  useEffect(() => {
    if (loadingState === 'delivery-list') {
      fetchSessions();
    }
  }, [loadingState, fetchSessions]);

  // Fetch active session detail for checklist
  const fetchSessionDetail = useCallback(async (sessionId: number) => {
    try {
      const response = await api.get(`/wms/loading-sessions/${sessionId}`);
      if (response.data.success && response.data.data) {
        setActiveSession(response.data.data);
      }
    } catch {
      // Silent fail — keep existing data
    }
  }, []);

  // Poll session detail every 5 seconds while in session-active state
  useEffect(() => {
    if (loadingState !== 'session-active' || !activeSessionId) return;

    const interval = setInterval(() => {
      fetchSessionDetail(activeSessionId);
    }, 5000);

    return () => clearInterval(interval);
  }, [loadingState, activeSessionId, fetchSessionDetail]);

  const handleStartLoading = useCallback((customerCode: string) => {
    setDialogCustomer(customerCode);
    setLicensePlate('');
    setShowStartDialog(true);
  }, []);

  const handleResumeSession = useCallback((sessionId: number) => {
    setActiveSessionId(sessionId);
    activeSessionIdRef.current = sessionId;
    setLoadingState('session-active');
    loadingStateRef.current = 'session-active';
    fetchSessionDetail(sessionId);
  }, [fetchSessionDetail]);

  const handleConfirmStart = useCallback(async () => {
    try {
      const response = await api.post('/wms/loading-sessions', {
        customer_code: dialogCustomer,
        license_plate: licensePlate,
        operator_name: operatorName,
      });

      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setActiveSessionId(data.session_id || data.id);
        activeSessionIdRef.current = data.session_id || data.id;
        setActiveSession(data);
        setShowStartDialog(false);
        setLoadingState('session-active');
        loadingStateRef.current = 'session-active';
        // Fetch full session detail
        fetchSessionDetail(data.session_id || data.id);
      } else {
        setErrorMessage(response.data.error || 'Failed to start loading session');
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to start loading session';
      setErrorMessage(msg);
    }
  }, [dialogCustomer, licensePlate, operatorName, fetchSessionDetail]);

  const handlePalletScan = useCallback(async (sscc: string) => {
    const sessionId = activeSessionIdRef.current;
    if (!sessionId) return;

    try {
      const response = await api.post(`/wms/pallets/${sscc}/load`, {
        operator_name: operatorName,
        scan_method: 'barcode',
      });

      if (response.data.success) {
        // Refresh session detail to update checklist
        fetchSessionDetail(sessionId);
      } else {
        setErrorMessage(response.data.error || 'Failed to record loading scan');
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to record loading scan';
      setErrorMessage(msg);
    }
  }, [operatorName, fetchSessionDetail]);

  const handleScan = useCallback((code: string) => {
    const state = loadingStateRef.current;
    const isSSCC = code.length >= 18 && /^\d+$/.test(code);

    if ((state === 'session-active' || state === 'complete-check') && isSSCC) {
      handlePalletScan(code);
    }
  }, [handlePalletScan]);

  useBarcodeScanner({ minLength: 4, onScan: handleScan });

  const handleCompleteLoading = useCallback(() => {
    setLoadingState('signature');
    loadingStateRef.current = 'signature';
  }, []);

  const handleConfirmSignature = useCallback(async () => {
    const sessionId = activeSessionIdRef.current;
    if (!sessionId || !signaturePadRef.current) return;

    const signatureData = signaturePadRef.current.toDataURL();

    try {
      const response = await api.post(`/wms/loading-sessions/${sessionId}/complete`, {
        signature_data: signatureData,
      });

      if (response.data.success) {
        setLoadingState('done');
        loadingStateRef.current = 'done';

        // Auto-return to delivery list after 3000ms
        setTimeout(() => {
          setLoadingState('delivery-list');
          loadingStateRef.current = 'delivery-list';
          setActiveSessionId(null);
          activeSessionIdRef.current = null;
          setActiveSession(null);
        }, 3000);
      } else {
        setErrorMessage(response.data.error || 'Failed to complete loading session');
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to complete loading session';
      setErrorMessage(msg);
    }
  }, []);

  const handleEndSession = useCallback(() => {
    setShowEndDialog(false);
    setLoadingState('delivery-list');
    loadingStateRef.current = 'delivery-list';
    setActiveSessionId(null);
    activeSessionIdRef.current = null;
    setActiveSession(null);
  }, []);

  // Compute delivery stats
  const totalDeliveries = deliveries.length;
  const inProgressCount = deliveries.filter(d => d.status === 'in_progress').length;
  const readyCount = deliveries.filter(d => d.status === 'ready').length;

  // Compute pallet progress for active session
  const pallets = activeSession?.pallets || [];
  const scannedCount = pallets.filter(p => p.scanned_at !== null).length;
  const totalPallets = activeSession?.expected_pallets || pallets.length;
  const allScanned = totalPallets > 0 && scannedCount >= totalPallets;
  const remainingPallets = totalPallets - scannedCount;
  const progressPercent = totalPallets > 0 ? (scannedCount / totalPallets) * 100 : 0;

  const today = new Date().toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });



  return (
    <Box
      sx={{
        maxWidth: { xs: 800, md: 1200 },
        mx: 'auto',
        width: '100%',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Delivery List State */}
      {loadingState === 'delivery-list' && (
        <>
          {/* Today's date — layout provides the page title */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Typography variant="body1" color="text.secondary">
              {today}
            </Typography>
          </Box>

          {/* Summary strip */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              display: 'flex',
              justifyContent: 'space-around',
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {totalDeliveries}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Deliveries
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#F57C00' }}>
                {inProgressCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                In Progress
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {readyCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Ready
              </Typography>
            </Box>
          </Paper>

          {/* Delivery cards */}
          {deliveries.length === 0 ? (
            <Paper
              elevation={0}
              sx={{ p: 4, textAlign: 'center', backgroundColor: '#FFFFFF', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                No deliveries scheduled
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Deliveries appear here when pick tasks are generated from the despatch schedule
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {deliveries.map((delivery) => (
                <Grid item xs={12} md={6} key={delivery.customer_code}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      backgroundColor: '#FFFFFF',
                      borderRadius: '12px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      borderLeft: `4px solid ${delivery.status === 'in_progress' ? '#F57C00' : '#CB6015'}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {delivery.customer_code}
                      </Typography>
                      {delivery.status === 'in_progress' ? (
                        <Chip label="In Progress" sx={{ backgroundColor: '#F57C00', color: '#FFFFFF' }} />
                      ) : (
                        <Chip label="Ready" variant="outlined" sx={{ borderColor: '#CB6015', color: '#CB6015' }} />
                      )}
                    </Box>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                      {delivery.loaded_count}/{delivery.total_pallets} pallets loaded
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" color="text.secondary">
                        {delivery.license_plate || ''}
                      </Typography>
                      {delivery.session_id ? (
                        <Button
                          variant="contained"
                          sx={{ backgroundColor: '#CB6015' }}
                          onClick={() => handleResumeSession(delivery.session_id!)}
                        >
                          Resume
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          sx={{ backgroundColor: '#CB6015' }}
                          onClick={() => handleStartLoading(delivery.customer_code)}
                        >
                          Start Loading
                        </Button>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* Session Active State */}
      {(loadingState === 'session-active' || loadingState === 'complete-check') && activeSession && (
        <>
          {/* Session header */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: '2px solid #CB6015',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {activeSession.customer_code}
            </Typography>
            <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 1 }}>
              {activeSession.license_plate}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: allScanned ? '#2E7D32' : '#CB6015',
                mb: 0.5,
              }}
            >
              {scannedCount} of {totalPallets} loaded
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                height: 8,
                borderRadius: 4,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#CB6015',
                },
              }}
            />
          </Paper>

          {/* Pallet checklist */}
          <Box sx={{ mb: 3 }}>
            {pallets.map((pallet, idx) => {
              const isScanned = pallet.scanned_at !== null;
              return (
                <Box
                  key={pallet.sscc || idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    p: 1.5,
                    mb: 1,
                    borderRadius: 1,
                    backgroundColor: isScanned ? '#E8F5E9' : '#FFFFFF',
                    borderLeft: `3px solid ${isScanned ? '#2E7D32' : '#E0E0E0'}`,
                  }}
                >
                  <Box sx={{ mt: 0.5, mr: 1.5, flexShrink: 0 }}>
                    {isScanned ? (
                      <CheckCircle sx={{ color: '#2E7D32' }} />
                    ) : (
                      <RadioButtonUnchecked sx={{ color: '#E0E0E0' }} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                      <Typography variant="body1" sx={{ fontWeight: 700 }}>
                        {pallet.job_number || '—'}
                        {pallet.pallet_number != null && (
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            Pallet {pallet.pallet_number}
                          </Typography>
                        )}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, ml: 1, flexShrink: 0 }}>
                        {pallet.quantity?.toLocaleString() ?? '—'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>
                      {pallet.stock_code || '—'}
                      {pallet.product_description && ` — ${pallet.product_description}`}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {pallet.sscc}
                      </Typography>
                      {isScanned && pallet.scanned_at && (
                        <Typography variant="caption" color="text.secondary">
                          {new Date(pallet.scanned_at).toLocaleTimeString()}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Scan prompt — hidden on desktop */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: '#FFF3E0',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              textAlign: 'center',
              display: { xs: 'block', md: 'none' },
              transition: 'background-color 0.2s',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#CB6015' }}>
              SCAN NEXT PALLET
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Point scanner at pallet SSCC barcode
            </Typography>
          </Paper>

          {/* Manual barcode entry for desktop testing */}
          <Box sx={{ mb: 3 }}>
            <ManualScanInput onSubmit={handleScan} placeholder="Type or paste pallet SSCC..." />
          </Box>

          {/* Complete Loading button */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={!allScanned}
              onClick={handleCompleteLoading}
              sx={{
                backgroundColor: allScanned ? '#2E7D32' : '#E0E0E0',
                color: allScanned ? '#FFFFFF' : undefined,
                maxWidth: { xs: '100%', md: 300 },
                minHeight: { xs: 56, md: 48 },
                '&:hover': allScanned
                  ? { backgroundColor: '#1B5E20' }
                  : undefined,
                '&.Mui-disabled': {
                  backgroundColor: '#E0E0E0',
                },
              }}
            >
              {allScanned
                ? 'Complete Loading'
                : `Complete Loading (${remainingPallets} remaining)`}
            </Button>
          </Box>

          {/* End Session button */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Button
              variant="text"
              sx={{ color: '#9E9E9E' }}
              onClick={() => setShowEndDialog(true)}
            >
              End Session
            </Button>
          </Box>
        </>
      )}

      {/* Signature State */}
      {loadingState === 'signature' && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            Driver Signature
          </Typography>
          <Box sx={{ width: { xs: '100%', md: 500 }, mx: 'auto', mb: 2 }}>
            <SignaturePad ref={signaturePadRef} height={200} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              sx={{ color: '#9E9E9E', borderColor: '#9E9E9E' }}
              onClick={() => signaturePadRef.current?.clear()}
            >
              Clear Signature
            </Button>
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#2E7D32',
                color: '#FFFFFF',
                minHeight: 56,
                '&:hover': { backgroundColor: '#1B5E20' },
              }}
              onClick={handleConfirmSignature}
            >
              Confirm Signature
            </Button>
          </Box>
        </Paper>
      )}

      {/* Done State */}
      {loadingState === 'done' && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: '#E8F5E9',
            border: '2px solid #2E7D32',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            transition: 'background-color 0.2s',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#2E7D32', mb: 1 }}>
            Loading complete
          </Typography>
          {activeSession && (
            <Box>
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                {activeSession.customer_code}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {scannedCount} pallets loaded
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Start Loading Dialog */}
      <Dialog open={showStartDialog} onClose={() => setShowStartDialog(false)}>
        <DialogTitle>Start Loading Session</DialogTitle>
        <DialogContent>
          <Typography variant="h5" component="div" sx={{ fontWeight: 700, mb: 2 }}>
            {dialogCustomer}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            required
            label="Truck License Plate"
            value={licensePlate}
            onChange={(e) => setLicensePlate(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && licensePlate.trim()) handleConfirmStart();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStartDialog(false)}>
            Go Back
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: '#CB6015' }}
            disabled={!licensePlate.trim()}
            onClick={handleConfirmStart}
          >
            Start Session
          </Button>
        </DialogActions>
      </Dialog>

      {/* End Session Confirmation Dialog */}
      <Dialog open={showEndDialog} onClose={() => setShowEndDialog(false)}>
        <DialogTitle>End Loading Session</DialogTitle>
        <DialogContent>
          <Typography>
            End loading session for {activeSession?.customer_code}? {remainingPallets} pallets have not been scanned.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEndDialog(false)}>Cancel</Button>
          <Button onClick={handleEndSession} color="error">
            End Session
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => setErrorMessage(null)}
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
